import type { SkillTemplate } from './issue-log-xref.js';

export const canaryBasicTemplate: SkillTemplate = {
  name: 'canary-basic',
  description: 'Learn the Canary CLI — commands, help, and common patterns',
  version: '1.0.0',
  render: () => `---
name: canary-basic
version: 1.0.0
description: Reference guide for the Canary CLI. Invoke when anyone mentions canary, canary CLI, or asks about CLI commands for testing, browser sessions, workflows, issues, or releases.
allowed-tools: Read, Grep, Glob
---

# Canary CLI Guide

The \`canary\` CLI is a command-line tool for running tests, controlling browser sessions, managing workflows, and inspecting issues.

## Install & Login

\`\`\`bash
# Install globally
npm install -g @canaryai/cli
# or
bun add -g @canaryai/cli

# Authenticate (opens browser)
canary login

# Login to a specific org (for multi-org users)
canary login --org "My Org"
\`\`\`

### Environment switching

\`\`\`bash
# Production (default)
canary login

# Dev
canary login --app-url https://app.dev.trycanary.ai --api-url https://api.dev.trycanary.ai

# Local
canary login --app-url http://localhost:5173 --api-url http://localhost:3000

# Or set the env var
export CANARY_API_URL=http://localhost:3000
\`\`\`

---

## Command Reference

### Test Suites

\`\`\`bash
# Trigger a test suite and stream results
canary test-suite run
canary test-suite run --tag smoke
canary test-suite run --property "My App" --environment staging --verbose

# Query test suite results
canary test-suite list
canary test-suite get <id> --failures-only
canary test-suite diagnostics <workflowId> <runId>
canary test-suite steps <workflowId> <runId>
\`\`\`

Run options: \`--token\`, \`--property\`, \`--environment\`, \`--tag\`, \`--name-pattern\`, \`--verbose\`

---

### Browser Sessions

Start and control browser sessions for interactive testing.

#### Session lifecycle

\`\`\`bash
canary session start                          # Start a new session
canary session start --credential "admin"     # Start with a credential
canary session start --url https://example.com --headless
canary session list                           # List active sessions
canary session status                         # Show session details
canary session stop                           # Stop the current session
canary session stop --all                     # Stop all sessions
canary session load-credential                # Load a credential into a running session
canary session allow-screenshots              # Grant Claude auto-read for screenshots
\`\`\`

#### Browser actions

\`\`\`bash
# Navigation
canary session navigate --url https://example.com
canary session back

# Observe the page
canary session snapshot                       # Get accessibility tree
canary session snapshot --mode screenshot     # Get a screenshot
canary session snapshot --mode both           # Tree + screenshot
canary session snapshot --search "Login"      # Search for text
canary session snapshot --expand e5           # Expand a collapsed ref
canary session screenshot --full-page         # Full-page screenshot

# Interact with elements (use ref from snapshot)
canary session click --ref e12
canary session type --ref e8 --text "hello" --submit
canary session fill --fields '[{"ref":"e1","value":"user@example.com"},{"ref":"e2","value":"pass123"}]'
canary session select --ref e15 --value "option-1"
canary session hover --ref e3
canary session press --key Enter
canary session scroll --direction down --amount 3

# Tabs
canary session tabs                           # List tabs
canary session tabs --action new              # Open new tab
canary session tabs --action select --index 0 # Switch to tab
canary session tabs --action close            # Close current tab

# Debugging
canary session wait --text "Success" --timeout 5000
canary session console --only-errors          # Show console messages
canary session network                        # Show network requests
canary session evaluate --js "document.title" # Run JavaScript
\`\`\`

Common options: \`--session <id|name>\` (target a specific session), \`--json\`, \`--env\`

#### Control (for cloud-connected sessions)

\`\`\`bash
canary session take-control     # Force CLI control (pauses cloud)
canary session resume-cloud     # Resume cloud execution
\`\`\`

---

### Recording

Record browser interactions for creating test workflows.

\`\`\`bash
canary record
canary record --credential admin --url https://example.com
canary record --output ./recordings --no-upload   # Save locally only
\`\`\`

Options: \`--credential\`, \`--url\`, \`--output\`, \`--env\`, \`--no-upload\`

---

### Workflows

Inspect and manage workflow definitions.

\`\`\`bash
# List and search
canary workflow list
canary workflow list --search "checkout" --status approved --json

# Get workflow definition
canary workflow get <workflowId> --format markdown
canary workflow get <workflowId> --json

# Create from file or stdin (new workflows start as draft)
canary workflow create --from-file workflow.json
echo '<workflow JSON>' | canary workflow create --from-stdin --property "My App"

# Approve (human-verified); once approved and passing, it's eligible for suites
canary workflow approve <workflowId> --yes

# Update workflow
canary workflow patch <workflowId> --name "New Name" --status approved

# Run a workflow
canary workflow run <workflowId> --stream

# Replace all nodes/edges
canary workflow sync <workflowId> --from-file updated.json

# Authoring guide
canary workflow guide
canary workflow guide selectors
\`\`\`

List filters: \`--search\`, \`--flow-type\` (standard|setup|seed|teardown), \`--status\` (draft|approved), \`--page\`, \`--page-size\`

Workflow statuses: \`draft\` = editable, not yet human-verified. \`approved\` = a human verified the test checks the right thing. Inclusion in regression runs is derived eligibility: an approved flow that has also passed at least once is eligible and runs in suites — there is no separate promote step. \`canary workflow pause <id>\` excludes an eligible flow from suite runs and \`canary workflow unpause <id>\` re-includes it. Reliability is tracked automatically by the \`health\` dimension — a flow that keeps failing in suites becomes \`degraded\` and is skipped (shown "degraded — not run") until it's edited or manually re-enabled.

---

### Issues

Search and inspect QA issues detected by Canary.

\`\`\`bash
# List issues
canary issues list
canary issues list --severity high --status open --format markdown
canary issues list --search "timeout" --property-id <uuid>

# Get issue details
canary issues get <issue-id>
canary issues get <issue-id> --format markdown
\`\`\`

List options: \`--search\`, \`--severity\` (low|medium|high|unknown), \`--status\` (open|closed|not_a_bug), \`--property-id\`, \`--page\`, \`--page-size\`, \`--json\`, \`--format markdown\`

---

### Release QA (CI/CD)

Trigger and monitor Release QA runs from CI pipelines.

\`\`\`bash
canary release trigger --property-id <uuid>
canary release status <run-id> --json
canary release run --property-id <uuid> --timeout 600   # Trigger and poll
\`\`\`

---

### Properties & Credentials

\`\`\`bash
canary property list                    # List properties and linked repos
canary credential list                  # List credentials with environment info
\`\`\`

---

### Superadmin Commands

These commands require superadmin access.

\`\`\`bash
# Knobs (global runtime config)
canary knobs list
canary knobs get <key>
canary knobs set <key> <value> --type boolean|number|string
canary knobs toggle <key>
canary knobs delete <key>
canary knobs lifecycle <key> --stage <stage>

# Feature flags (per-org gating)
canary feature-flag list
canary feature-flag create <name> --description "..."
canary feature-flag enable <name> --target-org <orgId>
canary feature-flag disable <name> --target-org <orgId>
canary feature-flag delete <name>
canary feature-flag lifecycle <name> --stage <stage>

# Database (read-only)
canary psql "SELECT * FROM knobs LIMIT 5" --json

# Redis (read-only)
canary redis "GET some:key" --json

# Documentation pages
canary docs <sub-command>

# Debug sessions
canary debug-session --env dev --json
\`\`\`

---

## Common Patterns

| Pattern | Description |
|---------|-------------|
| \`--json\` | Raw JSON output (most commands) |
| \`--format markdown\` | Markdown-formatted output (issues, workflows) |
| \`--env <env>\` | Target environment: \`local\`, \`dev\`, \`prod\` |
| \`--verbose, -v\` | Show all events / detailed output |
| \`-h, --help\` | Show help for any command |

### Environment variables

| Variable | Default | Description |
|----------|---------|-------------|
| \`CANARY_API_URL\` | \`https://api.runcofactor.com\` | API endpoint |
| \`CANARY_APP_URL\` | \`https://app.runcofactor.com\` | App URL for login |
| \`CANARY_API_TOKEN\` | — | API key (alternative to \`canary login\`) |

## Getting Help

\`\`\`bash
canary help              # Show all commands
canary <command> --help  # Show help for a specific command
canary version           # Show CLI version
canary upgrade           # Upgrade to latest version
\`\`\`
`,
};
